import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useRegisterSingleActiveMedia,
  useSingleActiveMedia,
} from "../../hooks/useSingleActiveMedia";

const MediaSection = ({ media, onLoadMore, hasMore, isLoading }) => {
  const { register, claimActive, releaseActive } = useSingleActiveMedia();
  // Filter media by type
  const [images, setImages] = useState([]);
  const [videos, setVideo] = useState([]);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (media && Array.isArray(media)) {
      setImages(media.filter((item) => item.type === "image"));
      setVideo(media.filter((item) => item.type === "video"));
      setDocuments(media.filter((item) => item.type === "document"));
    }
  }, [media]);

  // Video state management
  const videoRefs = useRef([]);
  const [playingVideo, setPlayingVideo] = useState(null);

  const stopPlayback = useCallback(() => {
    videoRefs.current.forEach((video) => video?.pause?.());
    setPlayingVideo(null);
  }, []);

  useRegisterSingleActiveMedia(stopPlayback, register);

  const togglePlay = (index) => {
    const video = videoRefs.current[index];
    if (!video) return;

    if (playingVideo === index) {
      video.pause();
      setPlayingVideo(null);
      releaseActive();
    } else {
      claimActive(video);
      video.play().catch((err) => console.error("Video play failed:", err));
      setPlayingVideo(index);
    }
  };

  const handleVideoClick = (index) => {
    togglePlay(index);
  };

  const handleVideoEnded = () => {
    setPlayingVideo(null);
    releaseActive();
  };

  if (!media || media.length === 0) {
    return (
      <div
        className="journal-card"
        style={{
          border: "none",
          marginTop: "-24px",
          marginBottom: "31px",
          padding: "20px",
          color: "#D0D8DF",
          textAlign: "center",
        }}
      >
        <div className="text-heading-h3" style={{ fontSize: "20px" }}>
          Media (0)
        </div>
        <p style={{ marginTop: "16px" }}>No media available for this group</p>
      </div>
    );
  }

  return (
    <div>
      <div
        className="journal-card"
        style={{
          border: "none",
          marginTop: "-24px",
          marginBottom: "31px",
          padding: "20px",
          color: "#D0D8DF",
        }}
      >
        {/* Images Section */}
        {images.length > 0 && (
          <>
            <div
              className="text-heading-h3"
              style={{ fontSize: "20px", marginBottom: "16px" }}
            >
              Images ({images.length})
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                gap: "10px",
                marginBottom: "24px",
              }}
            >
              {images.map((item, index) => (
                <div key={`image-${index}`} style={{ position: "relative" }}>
                  <img
                    src={item.url || "images/post-image.png"}
                    alt={`Image ${index + 1}`}
                    style={{
                      height: "auto",
                      maxHeight: "200px",
                      width: "100%",
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Videos Section */}
        {videos.length > 0 && (
          <>
            <div
              className="text-heading-h3"
              style={{ fontSize: "20px", marginBottom: "16px" }}
            >
              Videos ({videos.length})
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                gap: "10px",
                marginBottom: "24px",
              }}
            >
              {videos.map((item, index) => (
                <div
                  key={`video-${index}`}
                  style={{
                    position: "relative",
                    cursor: "pointer",
                  }}
                  onClick={() => handleVideoClick(index)}
                >
                  <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    style={{
                      height: "auto",
                      maxHeight: "200px",
                      width: "100%",
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                    onEnded={handleVideoEnded}
                    muted
                  >
                    <source src={item.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  {/* Play/Pause overlay */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        playingVideo === index
                          ? "transparent"
                          : "rgba(0,0,0,0.5)",
                      borderRadius: "8px",
                    }}
                  >
                    {playingVideo !== index && (
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Documents Section */}
        {documents.length > 0 && (
          <>
            <div
              className="text-heading-h3"
              style={{ fontSize: "20px", marginBottom: "16px" }}
            >
              Documents ({documents.length})
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                gap: "10px",
              }}
            >
              {documents.map((item, index) => (
                <div
                  key={`doc-${index}`}
                  style={{
                    position: "relative",
                    cursor: "pointer",
                    backgroundColor: "#2A4052",
                    borderRadius: "8px",
                    padding: "10px",
                    textAlign: "center",
                  }}
                  onClick={() => window.open(item.url, "_blank")}
                >
                  <img
                    src="/icons/pdf-icon.svg"
                    alt="Document"
                    style={{
                      width: "48px",
                      height: "48px",
                      marginBottom: "8px",
                    }}
                  />
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                    }}
                  >
                    {item.url.split("/").pop()}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {hasMore && (
          <button
            onClick={onLoadMore}
            disabled={isLoading}
            style={{
              display: "block",
              margin: "20px auto 0",
              padding: "10px 20px",
              backgroundColor: "#008CFF",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            {isLoading ? "Loading..." : "Load More"}
          </button>
        )}
      </div>
    </div>
  );
};

export default MediaSection;
